import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, Fragment as _Fragment, createVNode as _createVNode } from "vue";
const _hoisted_1 = {
    key: 2,
    "data-testid": "user-roles-catalogue-error",
    role: "alert",
    class: "text-sm text-(--colors-text-error) mt-2"
};
const _hoisted_2 = {
    key: 0,
    "data-testid": "user-roles-validation-error",
    role: "alert",
    class: "text-sm text-(--colors-text-error) mt-2"
};
const _hoisted_3 = {
    key: 1,
    "data-testid": "user-roles-save-error",
    role: "alert",
    class: "text-sm text-(--colors-text-error) mt-2"
};
const _hoisted_4 = {
    key: 2,
    role: "status",
    "aria-live": "polite",
    class: "sr-only",
    "data-testid": "user-roles-saving-status"
};
import { ref, computed, watch, onBeforeUnmount, onMounted, unref } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteLeave, onBeforeRouteUpdate, } from "vue-router";
import { UnsavedChangesModal } from "@eudonet/opal";
import { useUsersAdminStore } from "../../stores/useUsersAdminStore";
import { isSelectableRole, buildRoleModuleGroups } from "../../domain/entities/Role";
import { useAppToast } from "@/shared/composables/useAppToast";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useKeycloak } from "@/plugins/keycloak";
import { logger } from "@/infrastructure/services/LoggerService";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import UserRolesCell from "./UserRolesCell.vue";
import UserRolesMatrix from "./UserRolesMatrix.vue";
const ACCESS_MANAGER_ROLE = "AccessManager";
/** BFF error codes (dotted, OPUS-emitted) → i18n keys, resolved via `resolveApiErrorKey`. */
export default /*@__PURE__*/ _defineComponent({
    __name: 'UserRolesEditor',
    props: {
        userId: {},
        roles: {},
        assignedRoles: {}
    },
    emits: ["saved"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const ROLE_SAVE_ERROR_KEYS = {
            "User.Role.SelfAccessManagerRemoval": "accessManager.userDetail.roles.cannotRemoveOwnAccessManager",
        };
        const props = __props;
        const emit = __emit;
        const store = useUsersAdminStore();
        const { toastSuccess, toastError } = useAppToast();
        const { t } = useI18n();
        const { canManageUsers } = usePermissions();
        const keycloak = useKeycloak();
        // The live, pending selection — toggled directly from the matrix. Resynced to props.roles
        // whenever it changes (a fresh save, or switching to a different user via the route param).
        const selectedRoles = ref([...props.roles]);
        watch(() => props.roles, (roles) => {
            selectedRoles.value = [...roles];
        });
        const catalogue = computed(() => unref(store.roleCatalogue)?.filter((r) => isSelectableRole(r.type)) ?? null);
        const catalogueFailed = ref(false);
        const showUnsavedChangesModal = ref(false);
        // Set while the modal is open (route-leave or dialog-close attempt); resolved by handleStay/handleLeave.
        const pendingNavigation = ref(null);
        const isSaving = computed(() => store.isSavingRoles);
        // unref: in production `store` is the real Pinia store, whose property access already
        // unwraps refs; in component tests the store is a plain mock object, so store.rolesError
        // may literally be a Ref. unref() handles both without the component knowing which.
        const rolesError = computed(() => unref(store.rolesError));
        // Resolved once in save()'s catch and reused for both the toast and the inline message
        // below, so the two surfaces never show different text for the same failure.
        const saveErrorKey = ref(undefined);
        // The logged-in user editing their own profile — used to block self-demotion (rule: an
        // Access Manager can never remove their own AccessManager role).
        const isEditingSelf = computed(() => {
            const currentUserId = keycloak.keycloakInstance?.tokenParsed?.sub;
            return !!currentUserId && currentUserId === props.userId;
        });
        // The AccessManager row stays visible but is not togglable when editing your own profile —
        // enforced here (a real `disabled` button, unlike the old MultipleSelect option flag), with
        // isSelfDemotionBlocked + the backend guard as the authoritative backstop.
        const lockedTypes = computed(() => (isEditingSelf.value ? [ACCESS_MANAGER_ROLE] : []));
        // Order-independent: toggling is set membership, not list order.
        const isRolesChanged = computed(() => {
            if (selectedRoles.value.length !== props.roles.length)
                return true;
            const original = new Set(props.roles);
            return selectedRoles.value.some((role) => !original.has(role));
        });
        const isSelfDemotionBlocked = computed(() => isEditingSelf.value && !selectedRoles.value.includes(ACCESS_MANAGER_ROLE));
        const validationError = computed(() => {
            if (selectedRoles.value.length === 0) {
                return t("accessManager.userDetail.roles.atLeastOneRequired");
            }
            if (isSelfDemotionBlocked.value) {
                return t("accessManager.userDetail.roles.cannotRemoveOwnAccessManager");
            }
            return undefined;
        });
        // The role matrix, grouped by module, reflecting the LIVE pending selection — empty while
        // the catalogue hasn't loaded yet (or failed), in which case the template falls back to the
        // flat UserRolesCell chip list (always built from the last saved props.roles).
        const roleModuleGroups = computed(() => catalogue.value ? buildRoleModuleGroups(catalogue.value, selectedRoles.value) : []);
        async function loadCatalogue() {
            try {
                await store.loadRoleCatalogue();
            }
            catch (err) {
                catalogueFailed.value = true;
                logger.error("Failed to load role catalogue:", err);
            }
        }
        // True while the catalogue is still loading, so the hosting dialog shows a disabled Save from
        // the first frame instead of adding it once the matrix arrives.
        const isEditable = computed(() => canManageUsers.value && !catalogueFailed.value);
        const canSave = computed(() => isEditable.value &&
            roleModuleGroups.value.length > 0 &&
            !isSaving.value &&
            selectedRoles.value.length > 0 &&
            isRolesChanged.value &&
            !isSelfDemotionBlocked.value);
        onMounted(() => {
            // The store outlives this editor: each dialog opening mounts a fresh one.
            store.resetRolesError();
            void loadCatalogue();
        });
        function handleToggle(type) {
            if (!canManageUsers.value || lockedTypes.value.includes(type))
                return;
            selectedRoles.value = selectedRoles.value.includes(type)
                ? selectedRoles.value.filter((role) => role !== type)
                : [...selectedRoles.value, type];
        }
        // beforeRouteUpdate covers a param-only change (user A to user B through history), which reuses
        // the detail view and so never triggers beforeRouteLeave.
        const guardUnsavedChanges = (_to, _from, next) => {
            if (!isRolesChanged.value) {
                next();
                return;
            }
            pendingNavigation.value = { proceed: () => next(), cancel: () => next(false) };
            showUnsavedChangesModal.value = true;
        };
        onBeforeRouteLeave(guardUnsavedChanges);
        onBeforeRouteUpdate(guardUnsavedChanges);
        // Unmounted under an open prompt, the pending changes are gone anyway: let the waiting close or
        // navigation through, or the hosting dialog's requestClose() never settles.
        onBeforeUnmount(() => {
            const navigation = pendingNavigation.value;
            pendingNavigation.value = null;
            navigation?.proceed();
        });
        /** Lets a hosting dialog ask before closing: resolves false while the user chooses to stay. */
        function requestClose() {
            if (!isRolesChanged.value)
                return Promise.resolve(true);
            return new Promise((resolve) => {
                pendingNavigation.value = { proceed: () => resolve(true), cancel: () => resolve(false) };
                showUnsavedChangesModal.value = true;
            });
        }
        __expose({ requestClose, save, canSave, isEditable, isSaving });
        function handleLeave() {
            showUnsavedChangesModal.value = false;
            const navigation = pendingNavigation.value;
            pendingNavigation.value = null;
            navigation?.proceed();
        }
        function handleStay() {
            showUnsavedChangesModal.value = false;
            const navigation = pendingNavigation.value;
            pendingNavigation.value = null;
            navigation?.cancel();
        }
        async function save() {
            if (selectedRoles.value.length === 0 || isSelfDemotionBlocked.value)
                return;
            try {
                await store.updateUserRoles(props.userId, selectedRoles.value);
                toastSuccess("accessManager.userDetail.permissions.saveSuccess");
                emit("saved");
            }
            catch (err) {
                saveErrorKey.value = resolveApiErrorKey(err, ROLE_SAVE_ERROR_KEYS, "accessManager.userDetail.permissions.saveFailed");
                toastError(saveErrorKey.value);
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                (roleModuleGroups.value.length > 0)
                    ? (_openBlock(), _createBlock(UserRolesMatrix, {
                        key: 0,
                        groups: roleModuleGroups.value,
                        editable: _unref(canManageUsers),
                        "locked-types": lockedTypes.value,
                        onToggle: handleToggle
                    }, null, 8 /* PROPS */, ["groups", "editable", "locked-types"]))
                    : (_openBlock(), _createBlock(UserRolesCell, {
                        key: 1,
                        roles: __props.assignedRoles,
                        "max-visible": Math.max(__props.assignedRoles.length, 1)
                    }, null, 8 /* PROPS */, ["roles", "max-visible"])),
                (_unref(canManageUsers) && catalogueFailed.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_ctx.$t("accessManager.userDetail.roles.loadCatalogueFailed")), 1 /* TEXT */))
                    : _createCommentVNode("v-if", true),
                (_unref(canManageUsers) && roleModuleGroups.value.length > 0)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                        (validationError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(validationError.value), 1 /* TEXT */))
                            : (rolesError.value)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t(saveErrorKey.value ?? "accessManager.userDetail.permissions.saveFailed")), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true),
                        (isSaving.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_ctx.$t("accessManager.userDetail.permissions.savingStatus")), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ], 64 /* STABLE_FRAGMENT */))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _ctx.$t('unsavedChanges.title'),
                    message: _ctx.$t('unsavedChanges.message'),
                    "stay-label": _ctx.$t('unsavedChanges.stay'),
                    "leave-label": _ctx.$t('unsavedChanges.leave'),
                    onStay: handleStay,
                    onLeave: handleLeave
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label"])
            ]));
        };
    }
});
